import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Sparkles,
  Phone,
  Mail,
  MessageCircle,
  Search,
  MapPin,
  Bot,
  Globe2,
  CheckCircle2,
  TrendingUp,
  Eye,
  Database,
  Star,
  ScanLine,
  Quote,
  Flame,
  Award,
  Layers,
  Activity,
  Repeat,
  Compass,
  Megaphone,
  ShieldCheck,
  BarChart3,
  ChevronRight,
  PieChart,
  Calendar,
  Headphones,
  ArrowUpRight,
} from 'lucide-react';
import SEO from '../components/SEO';
import ContinueExploring from '../components/ContinueExploring';
import IndexAiScanner from '../components/IndexAiScanner';
import CompetitorCompare from '../components/CompetitorCompare';
import { AffiliateStrip } from '../components/AffiliateStrip';
import { ProductPricingCTA, FlagshipStrip } from '../components/FlagshipFunnel';
import { useT } from '../i18n';
import { TEAM_AVATARS } from '../components/teamAvatars';
import {
  ChatGPTMark,
  OpenAIMark,
  GeminiMark,
  ClaudeMark,
  PerplexityMark,
  GrokMark,
  GoogleMark,
  MetaMark,
  ToastMark,
  SquareMark,
  DoorDashMark,
  UberEatsMark,
  OpenTableMark,
  YelpMark,
} from '../components/BrandIcons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

/* =================================================================
   Index AI — restaurant-first AI visibility flagship.
   Premium SaaS storytelling: hero → AI engines bar → How AI Search
   works → What Kitxens does → 6-step process → Dashboard mockup →
   Prompt examples → SEO comparison → Business results → Service
   highlights → Trust strip → CTA.
   ================================================================= */

const C = {
  en: {
    eyebrow: 'RESTAURANT VISIBILITY FOR THE AI ERA',
    heroTitle: 'Become the restaurant AI recommends.',
    heroBody: "Your future guests aren't only searching on Google. They're asking ChatGPT, Gemini, Perplexity, Claude, Google AI Overviews and Grok where to eat. Kitxens makes sure your restaurant is the answer.",
    heroSub: "We don't sell software you have to learn. Kitxens manages the entire AI visibility process — audit, optimization, content, monitoring — so your restaurant gets discovered everywhere guests are deciding.",
    ctaA: 'Get My AI Visibility Audit',
    ctaB: 'See How Guests Find You',

    // Definition / TL;DR block — written as a premium executive summary AI engines can quote
    defnEyebrow: 'WHAT IS INDEX AI',
    defnTitle: "AI is already recommending restaurants in your area. If it's not yours, this is why — and how we fix it.",
    tldr: 'Index AI is the managed AI visibility service from Kitxens. We make your restaurant discoverable across the new generation of search — ChatGPT, Gemini, Perplexity, Claude and Google AI Overviews — where your future guests are already asking where to eat. Unlike a software platform you have to learn, Kitxens runs every step for you: audit, structured data, content, monitoring and continuous optimization. The result is a restaurant that is found, trusted and recommended before your competitors are even considered.',
    defnCards: [
      {
        t: 'What is AI Search?',
        b: 'A new way diners decide where to eat. Instead of scrolling Google results, guests ask ChatGPT, Gemini, Perplexity or Google AI Overviews — and get one direct recommendation.',
      },
      {
        t: 'Why it changes restaurant discovery',
        b: 'AI engines summarize answers from trusted, structured sources. Restaurants that are properly indexed get cited. Restaurants that are not, get skipped — even if they rank on Google.',
      },
      {
        t: 'Why Kitxens manages it for you',
        b: 'You stay focused on guests and operations. A Kitxens specialist owns your AI visibility — audit, content, schema, monitoring and monthly reporting — as a continuous managed service.',
      },
    ],

    // FAQ — restaurant-owner questions, not technical SEO ones
    faqEyebrow: 'FAQ',
    faqTitle: 'Real restaurant-owner questions, answered.',
    faqBody: 'Everything you need to decide whether Index AI is the right next step for your restaurant.',
    faq: [
      {
        q: 'Will my restaurant actually appear inside ChatGPT and Gemini?',
        a: "Yes — that is exactly what Index AI delivers. We prepare your restaurant's data, menu, reviews and authority signals so AI engines understand and recommend you. Most restaurants see their first AI mentions within 6–8 weeks.",
      },
      {
        q: 'Do I need to learn any software or operate a dashboard?',
        a: 'No. Kitxens is a managed service. Your dedicated specialist runs the entire program — audit, optimization, content, monitoring and monthly reporting. You stay focused on guests and operations.',
      },
      {
        q: 'How is this different from the SEO I already pay for?',
        a: "Traditional SEO ranks your website inside Google's blue-link results. Index AI prepares your restaurant to be the answer when a guest asks an AI assistant directly — across ChatGPT, Gemini, Perplexity, Claude and Google AI Overviews. They are complementary, and Kitxens handles both as one program.",
      },
      {
        q: 'What if I have multiple locations?',
        a: 'Index AI is built for multi-location restaurants and groups. Every location gets its own structured data, menu, reviews and local signals — so each one ranks independently in its market and AI engines surface the closest match for each guest query.',
      },
      {
        q: 'How quickly will I see results?',
        a: 'Most restaurants see measurable wins (more direct calls, more Map Pack appearances and first AI citations) within 30–60 days. Compounding visibility continues building over months 3 to 6.',
      },
      {
        q: 'Are guests really finding restaurants through AI today?',
        a: 'They already are. A growing share of younger diners now uses AI assistants to discover restaurants — and that share is compounding every quarter. The restaurants that get properly indexed today will own the next decade of discovery.',
      },
      {
        q: 'Will Index AI work with my current website and POS?',
        a: 'Yes. Index AI layers on top of the systems you already run — Toast, Square, Clover, Lightspeed, WordPress, Webflow and many more. We integrate, we do not rip-and-replace.',
      },
      {
        q: 'What happens if I decide to cancel?',
        a: 'You keep every optimization we shipped — the structured data, content and directory updates remain on your assets. We simply stop the ongoing monitoring and tuning. No lock-in, no penalties.',
      },
    ],

    // AI engines bar
    enginesTitle: 'One optimization. Every AI search engine.',
    enginesBody: 'We prepare your restaurant to be understood, trusted and recommended by the AI engines guests already use every day.',
    engines: [
      { name: 'ChatGPT', tone: 'from-emerald-500 to-teal-500' },
      { name: 'Gemini', tone: 'from-blue-500 to-sky-500' },
      { name: 'Claude', tone: 'from-orange-500 to-amber-500' },
      { name: 'Perplexity', tone: 'from-indigo-500 to-purple-500' },
      { name: 'Google AI', tone: 'from-rose-500 to-pink-500' },
      { name: 'Grok', tone: 'from-neutral-700 to-neutral-900' },
    ],

    // How AI Search works
    flowEyebrow: 'HOW AI SEARCH WORKS',
    flowTitle: 'From a guest question to your restaurant — in seconds.',
    flowBody: 'Guests no longer scroll through ten blue links. They ask one question and get one answer. Index AI makes sure your restaurant is that answer.',
    flow: [
      { i: Quote, t: 'Guest asks AI', b: '"Best brunch in San Jose"' },
      { i: Search, t: 'AI looks for trusted sources', b: 'Schema · reviews · authority' },
      { i: Layers, t: 'Kitxens prepares your restaurant', b: 'Audit · structure · content' },
      { i: Star, t: 'AI recommends your business', b: 'Top of the answer card' },
      { i: Calendar, t: 'More reservations & orders', b: 'Direct, not via aggregators' },
      { i: TrendingUp, t: 'More revenue', b: 'Compounded month over month' },
    ],

    // What Kitxens does
    servicesEyebrow: 'HOW KITXENS MAKES YOUR RESTAURANT VISIBLE',
    servicesTitle: 'A complete managed AI visibility service — built only for restaurants.',
    servicesBody: 'You stay focused on guests and operations. We do the AI work — every step, every month.',
    services: [
      {
        i: ShieldCheck,
        t: 'AI Readiness Audit',
        b: 'We verify your website, menu and brand are understandable by AI engines — and identify exactly what is blocking discovery.',
        tag: 'Audit',
      },
      {
        i: Database,
        t: 'Structured Data Optimization',
        b: 'We deploy Restaurant + Menu + LocalBusiness Schema.org markup AI engines actually trust — multi-location ready.',
        tag: 'Schema',
      },
      {
        i: Bot,
        t: 'AI Search Visibility',
        b: 'We increase the probability your restaurant is recommended inside ChatGPT, Gemini, Perplexity and Google AI Overviews.',
        tag: 'AI Search',
      },
      {
        i: Megaphone,
        t: 'Restaurant Content Strategy',
        b: 'We continuously publish menu, FAQ, neighborhood and offer content AI engines cite when guests ask about your area.',
        tag: 'Content',
      },
      {
        i: MapPin,
        t: 'Local Authority',
        b: 'We strengthen your Google Business Profile, citations, reviews and local signals — the foundation of AI trust.',
        tag: 'Local',
      },
      {
        i: Repeat,
        t: 'Continuous Optimization',
        b: 'We monitor your visibility every day across every surface and improve the signals that move the needle.',
        tag: 'Ops',
      },
    ],

    // Process
    processEyebrow: 'OUR PROCESS',
    processTitle: 'A six-step program. Designed for restaurant operators.',
    processBody: 'No DIY dashboards. No software to learn. Your dedicated specialist owns every step.',
    process: [
      { i: ShieldCheck, t: 'Audit', b: 'Full discovery — where you appear, where you should, what is broken.' },
      { i: Layers, t: 'Optimize', b: 'Structured data, content, menu and local signals deployed cleanly.' },
      { i: Megaphone, t: 'Publish', b: 'Restaurant-grade content shipped weekly to feed AI engines.' },
      { i: Activity, t: 'Monitor', b: 'Daily visibility tracking across Google, Maps and AI assistants.' },
      { i: Repeat, t: 'Improve', b: 'Continuous tuning based on what AI engines actually surface.' },
      { i: TrendingUp, t: 'Grow', b: 'More direct reservations, calls and orders — compounded monthly.' },
    ],

    // Dashboard
    dashEyebrow: 'AI VISIBILITY DASHBOARD',
    dashTitle: "You won't operate it. But you'll see exactly what we're doing.",
    dashBody: 'Every account gets a monthly visibility report covering AI mentions, citations, share-of-voice and the optimizations we shipped.',

    // Prompt examples
    promptsEyebrow: 'REAL GUEST QUERIES',
    promptsTitle: 'What guests already ask AI — and where we put your restaurant.',
    promptsBody: 'These are real prompt patterns that decide whether your restaurant gets the order tonight.',
    prompts: [
      { q: 'Best Mexican restaurant near Cupertino', a: 'Recommended by ChatGPT in Map Pack + AI Overview.' },
      { q: 'Best brunch in San Jose this weekend', a: 'Picked by Gemini for "family-friendly + outdoor seating".' },
      { q: 'Corporate catering in Mountain View for 50 people', a: 'Returned by Perplexity from your menu schema + reviews.' },
      { q: 'Family restaurant open right now', a: 'Surfaced in Google AI Overview with menu + hours card.' },
      { q: 'Best tacos near Cupertino under $15', a: 'Cited by ChatGPT directly from your structured menu data.' },
      { q: 'Romantic Italian restaurant near me', a: 'Featured by Claude using your reviews + price-range signals.' },
    ],

    // Comparison
    cmpEyebrow: 'TRADITIONAL SEO vs INDEX AI',
    cmpTitle: 'Ranking on Google is no longer enough.',
    cmpBody: 'Guests increasingly skip the search results and ask an AI directly. Index AI is the new playbook.',
    cmp: {
      old: {
        t: 'Traditional SEO',
        b: 'Ranks your website inside a list of blue links. The guest still has to choose between you and ten competitors.',
        items: [
          'Optimizes for keyword rankings',
          'Lives inside the Google results page',
          'Built for desktop browsing behavior',
          'Static content + slow iteration',
        ],
      },
      ai: {
        t: 'Index AI',
        b: 'Makes AI engines understand, trust and actively recommend your restaurant as the answer — across every assistant.',
        items: [
          'Optimizes for AI recommendation',
          'Lives inside ChatGPT, Gemini, Perplexity, Google AI',
          'Built for how guests actually decide today',
          'Continuous content + monthly tuning',
        ],
      },
    },

    // Business outcomes
    outcomesEyebrow: 'WHAT YOUR RESTAURANT GAINS',
    outcomesTitle: 'Visibility that becomes real revenue.',
    outcomes: [
      { i: Calendar, t: 'More reservations' },
      { i: Megaphone, t: 'More catering requests' },
      { i: Globe2, t: 'More direct traffic' },
      { i: MapPin, t: 'Higher local visibility' },
      { i: Phone, t: 'More phone calls' },
      { i: Eye, t: 'More website visits' },
      { i: Award, t: 'Stronger online authority' },
      { i: Flame, t: 'Long-term discoverability' },
    ],

    // Service highlights
    highlightsEyebrow: 'WHY KITXENS',
    highlightsTitle: 'A premium service. Restaurant-first. Human led.',
    highlights: [
      { i: Activity, t: '24/7 AI monitoring', b: 'We watch every AI engine, every day.' },
      { i: Repeat, t: 'Continuous optimization', b: 'Weekly tuning. Monthly content. Always shipping.' },
      { i: Sparkles, t: 'AI search ready', b: 'Structured for ChatGPT, Gemini, Perplexity and beyond.' },
      { i: Flame, t: 'Restaurant focused', b: 'Built for menus, locations, reservations and catering.' },
      { i: Compass, t: 'Google + AI visibility', b: 'Classic search and AI answers — one program.' },
      { i: Headphones, t: 'Human managed', b: 'A dedicated specialist owns your visibility roadmap.' },
    ],

    // Trust strip
    trustLabel: 'TRUSTED ECOSYSTEM',
    trust: ['Google', 'OpenAI', 'Meta', 'Toast', 'Square', 'DoorDash', 'Uber Eats', 'OpenTable', 'Google Business Profile'],

    // Final CTA
    ctaTitle: "Let's make your restaurant the answer.",
    ctaBody: 'A free 15-minute AI visibility audit will show you exactly where Kitxens moves your restaurant from invisible to top-of-results across AI search.',
    ctaPrimary: 'Get My AI Visibility Audit',
    ctaSecondary: 'Book My AI Strategy Call',
  },

  es: {
    eyebrow: 'VISIBILIDAD DE RESTAURANTES PARA LA ERA IA',
    heroTitle: 'Sé el restaurante que la IA recomienda.',
    heroBody: 'Tus futuros clientes ya no sólo buscan en Google. Le preguntan a ChatGPT, Gemini, Perplexity, Claude, Google AI Overviews y Grok dónde comer. Kitxens hace que tu restaurante sea la respuesta.',
    heroSub: 'No vendemos software que tengas que aprender. Kitxens gestiona el proceso completo de visibilidad IA — auditoría, optimización, contenido, monitoreo — para que tu restaurante sea descubierto donde se decide comer.',
    ctaA: 'Auditoría de Visibilidad IA',
    ctaB: 'Cómo te encuentran',

    defnEyebrow: 'QUÉ ES INDEX AI',
    defnTitle: 'La IA ya está recomendando restaurantes en tu zona. Si no es el tuyo, esta es la razón — y así lo arreglamos.',
    tldr: 'Index AI es el servicio gestionado de visibilidad IA de Kitxens. Hacemos que tu restaurante sea descubierto en la nueva generación de búsqueda — ChatGPT, Gemini, Perplexity, Claude y Google AI Overviews — donde tus próximos clientes ya están preguntando dónde comer. A diferencia de una plataforma de software que tienes que aprender, Kitxens opera cada paso por ti: auditoría, datos estructurados, contenido, monitoreo y optimización continua. El resultado es un restaurante que se encuentra, se confía y se recomienda antes de que tus competidores entren en la conversación.',
    defnCards: [
      { t: '¿Qué es la búsqueda con IA?', b: 'Una nueva forma de elegir dónde comer. En lugar de scrollear resultados de Google, los clientes le preguntan a ChatGPT, Gemini, Perplexity o Google AI Overviews — y reciben una recomendación directa.' },
      { t: 'Por qué cambia el descubrimiento', b: 'Los buscadores con IA resumen respuestas desde fuentes confiables y estructuradas. Los restaurantes bien indexados son citados. Los que no lo están quedan fuera — incluso si rankean en Google.' },
      { t: 'Por qué Kitxens lo gestiona por ti', b: 'Tú te enfocas en clientes y operación. Un especialista de Kitxens lleva tu visibilidad IA — auditoría, contenido, schema, monitoreo y reporte mensual — como un servicio gestionado continuo.' },
    ],

    faqEyebrow: 'FAQ',
    faqTitle: 'Preguntas reales de dueños de restaurante, respondidas.',
    faqBody: 'Todo lo que necesitas para decidir si Index AI es el siguiente paso correcto para tu restaurante.',
    faq: [
      { q: '¿Mi restaurante realmente va a aparecer en ChatGPT y Gemini?', a: 'Sí — eso es exactamente lo que entrega Index AI. Preparamos los datos, menú, reseñas y señales de autoridad para que los buscadores con IA te entiendan y te recomienden. La mayoría de restaurantes ve sus primeras menciones IA en 6–8 semanas.' },
      { q: '¿Necesito aprender software u operar un dashboard?', a: 'No. Kitxens es un servicio gestionado. Tu especialista dedicado lleva el programa completo — auditoría, optimización, contenido, monitoreo y reporte mensual. Tú te enfocas en tus clientes.' },
      { q: '¿En qué se diferencia del SEO que ya pago?', a: 'El SEO tradicional posiciona tu sitio dentro de los resultados azules de Google. Index AI prepara a tu restaurante para ser la respuesta cuando un cliente le pregunta directo a un asistente IA — en ChatGPT, Gemini, Perplexity, Claude y Google AI Overviews. Son complementarios, y Kitxens cubre ambos como un solo programa.' },
      { q: '¿Y si tengo varias sedes?', a: 'Index AI está pensado para restaurantes multi-local y grupos. Cada sede recibe sus propios datos estructurados, menú, reseñas y señales locales — para que cada una rankee de forma independiente en su mercado.' },
      { q: '¿Qué tan rápido veo resultados?', a: 'La mayoría ve wins medibles (más llamadas directas, más apariciones en Map Pack y primeras menciones IA) en 30–60 días. La visibilidad compuesta sigue creciendo durante los meses 3 a 6.' },
      { q: '¿Los clientes realmente están encontrando restaurantes con IA hoy?', a: 'Ya lo están haciendo. Una parte creciente de los comensales más jóvenes usa asistentes IA para descubrir dónde comer — y ese porcentaje crece cada trimestre. Los restaurantes que se indexan hoy serán los que dominen la próxima década del descubrimiento.' },
      { q: '¿Index AI funciona con mi sitio web y POS actuales?', a: 'Sí. Index AI se monta sobre los sistemas que ya usas — Toast, Square, Clover, Lightspeed, WordPress, Webflow y muchos más. Integramos, no reemplazamos.' },
      { q: '¿Qué pasa si decido cancelar?', a: 'Te quedas con cada optimización que enviamos — los datos estructurados, contenido y actualizaciones de directorios permanecen en tus assets. Simplemente paramos el monitoreo y ajuste continuo. Sin lock-in, sin penalidades.' },
    ],

    enginesTitle: 'Una optimización. Todos los buscadores con IA.',
    enginesBody: 'Preparamos tu restaurante para que sea entendido, confiable y recomendado por los buscadores IA que tus clientes ya usan a diario.',
    engines: [
      { name: 'ChatGPT', tone: 'from-emerald-500 to-teal-500' },
      { name: 'Gemini', tone: 'from-blue-500 to-sky-500' },
      { name: 'Claude', tone: 'from-orange-500 to-amber-500' },
      { name: 'Perplexity', tone: 'from-indigo-500 to-purple-500' },
      { name: 'Google AI', tone: 'from-rose-500 to-pink-500' },
      { name: 'Grok', tone: 'from-neutral-700 to-neutral-900' },
    ],

    flowEyebrow: 'CÓMO FUNCIONA LA BÚSQUEDA CON IA',
    flowTitle: 'De una pregunta del cliente a tu restaurante — en segundos.',
    flowBody: 'Los clientes ya no scrollean diez resultados. Hacen una pregunta y reciben una respuesta. Index AI logra que tu restaurante sea esa respuesta.',
    flow: [
      { i: Quote, t: 'El cliente pregunta a la IA', b: '"Mejor brunch en San José"' },
      { i: Search, t: 'La IA busca fuentes confiables', b: 'Schema · reseñas · autoridad' },
      { i: Layers, t: 'Kitxens prepara tu restaurante', b: 'Auditoría · estructura · contenido' },
      { i: Star, t: 'La IA recomienda tu negocio', b: 'En lo alto de la respuesta' },
      { i: Calendar, t: 'Más reservas y pedidos', b: 'Directos, no por agregadores' },
      { i: TrendingUp, t: 'Más ingresos', b: 'Compuestos mes a mes' },
    ],

    servicesEyebrow: 'CÓMO KITXENS TE HACE VISIBLE',
    servicesTitle: 'Un servicio gestionado de visibilidad IA — construido sólo para restaurantes.',
    servicesBody: 'Tú te enfocas en tus clientes y operación. Nosotros hacemos todo el trabajo IA — cada paso, cada mes.',
    services: [
      { i: ShieldCheck, t: 'Auditoría de Preparación IA', b: 'Verificamos que tu sitio web, menú y marca sean comprensibles por buscadores IA — e identificamos exactamente qué bloquea la visibilidad.', tag: 'Auditoría' },
      { i: Database, t: 'Optimización de Datos Estructurados', b: 'Implementamos schema Restaurant + Menu + LocalBusiness que los buscadores IA realmente confían — listo para multi-sede.', tag: 'Schema' },
      { i: Bot, t: 'Visibilidad en Buscadores IA', b: 'Aumentamos la probabilidad de que tu restaurante sea recomendado dentro de ChatGPT, Gemini, Perplexity y Google AI Overviews.', tag: 'AI Search' },
      { i: Megaphone, t: 'Estrategia de Contenido', b: 'Publicamos contenido de menú, FAQ, zona y ofertas que los buscadores IA citan cuando preguntan por tu área.', tag: 'Contenido' },
      { i: MapPin, t: 'Autoridad Local', b: 'Fortalecemos tu Perfil de Google Business, citas, reseñas y señales locales — la base de la confianza IA.', tag: 'Local' },
      { i: Repeat, t: 'Optimización Continua', b: 'Monitoreamos tu visibilidad diario en cada superficie y mejoramos las señales que mueven la aguja.', tag: 'Ops' },
    ],

    processEyebrow: 'NUESTRO PROCESO',
    processTitle: 'Un programa de seis pasos. Diseñado para operadores.',
    processBody: 'Sin dashboards DIY. Sin software que aprender. Tu especialista dedicado se encarga de cada paso.',
    process: [
      { i: ShieldCheck, t: 'Auditoría', b: 'Discovery completo — dónde apareces, dónde deberías, qué está roto.' },
      { i: Layers, t: 'Optimiza', b: 'Schema, contenido, menú y señales locales desplegados limpiamente.' },
      { i: Megaphone, t: 'Publica', b: 'Contenido nivel restaurante shippeado cada semana para alimentar a la IA.' },
      { i: Activity, t: 'Monitorea', b: 'Tracking diario de visibilidad en Google, Maps y asistentes IA.' },
      { i: Repeat, t: 'Mejora', b: 'Ajuste continuo en base a lo que los buscadores IA realmente muestran.' },
      { i: TrendingUp, t: 'Crece', b: 'Más reservas directas, llamadas y pedidos — compuesto cada mes.' },
    ],

    dashEyebrow: 'DASHBOARD DE VISIBILIDAD IA',
    dashTitle: 'No vas a operarlo. Pero vas a ver exactamente qué hacemos.',
    dashBody: 'Cada cuenta recibe un reporte mensual con menciones IA, citas, share-of-voice y las optimizaciones que enviamos.',

    promptsEyebrow: 'CONSULTAS REALES DE CLIENTES',
    promptsTitle: 'Lo que tus clientes ya le preguntan a la IA — y dónde ponemos a tu restaurante.',
    promptsBody: 'Estos son patrones reales de consulta que deciden si tu restaurante recibe el pedido esta noche.',
    prompts: [
      { q: 'Mejor restaurante mexicano cerca de Cupertino', a: 'Recomendado por ChatGPT en Map Pack + AI Overview.' },
      { q: 'Mejor brunch en San José este fin de semana', a: 'Elegido por Gemini por "familiar + terraza".' },
      { q: 'Catering corporativo en Mountain View para 50 personas', a: 'Devuelto por Perplexity desde tu menú schema + reseñas.' },
      { q: 'Restaurante familiar abierto ahora', a: 'En Google AI Overview con tarjeta de menú + horarios.' },
      { q: 'Mejores tacos cerca de Cupertino bajo $15', a: 'Citado por ChatGPT desde tu menú estructurado.' },
      { q: 'Restaurante italiano romántico cerca de mí', a: 'Destacado por Claude usando tus reseñas + rango de precios.' },
    ],

    cmpEyebrow: 'SEO TRADICIONAL vs INDEX AI',
    cmpTitle: 'Rankear en Google ya no alcanza.',
    cmpBody: 'Los clientes saltan cada vez más los resultados de búsqueda y le preguntan directo a una IA. Index AI es el nuevo playbook.',
    cmp: {
      old: {
        t: 'SEO Tradicional',
        b: 'Posiciona tu sitio dentro de una lista de links azules. El cliente todavía tiene que elegir entre tú y diez competidores.',
        items: ['Optimiza para keywords', 'Vive dentro del resultado de Google', 'Para conducta de escritorio', 'Contenido estático + iteración lenta'],
      },
      ai: {
        t: 'Index AI',
        b: 'Hace que los buscadores IA entiendan, confíen y recomienden activamente tu restaurante como la respuesta — en cada asistente.',
        items: ['Optimiza para recomendación IA', 'Vive dentro de ChatGPT, Gemini, Perplexity, Google AI', 'Para cómo los clientes deciden hoy', 'Contenido continuo + ajuste mensual'],
      },
    },

    outcomesEyebrow: 'LO QUE GANA TU RESTAURANTE',
    outcomesTitle: 'Visibilidad que se vuelve ingresos reales.',
    outcomes: [
      { i: Calendar, t: 'Más reservas' },
      { i: Megaphone, t: 'Más pedidos de catering' },
      { i: Globe2, t: 'Más tráfico directo' },
      { i: MapPin, t: 'Mayor visibilidad local' },
      { i: Phone, t: 'Más llamadas' },
      { i: Eye, t: 'Más visitas a la web' },
      { i: Award, t: 'Mayor autoridad online' },
      { i: Flame, t: 'Discoverability a largo plazo' },
    ],

    highlightsEyebrow: 'POR QUÉ KITXENS',
    highlightsTitle: 'Servicio premium. Pensado para restaurantes. Liderado por humanos.',
    highlights: [
      { i: Activity, t: 'Monitoreo IA 24/7', b: 'Vigilamos cada buscador IA, todos los días.' },
      { i: Repeat, t: 'Optimización continua', b: 'Tuning semanal. Contenido mensual. Siempre enviando.' },
      { i: Sparkles, t: 'Listo para buscadores IA', b: 'Estructurado para ChatGPT, Gemini, Perplexity y más.' },
      { i: Flame, t: 'Foco en restaurantes', b: 'Construido para menús, sedes, reservas y catering.' },
      { i: Compass, t: 'Visibilidad Google + IA', b: 'Búsqueda clásica y respuestas IA — un solo programa.' },
      { i: Headphones, t: 'Liderado por humanos', b: 'Un especialista dedicado dueño de tu hoja de ruta.' },
    ],

    trustLabel: 'ECOSISTEMA DE CONFIANZA',
    trust: ['Google', 'OpenAI', 'Meta', 'Toast', 'Square', 'DoorDash', 'Uber Eats', 'OpenTable', 'Google Business Profile'],

    ctaTitle: 'Hagamos que tu restaurante sea la respuesta.',
    ctaBody: 'Una auditoría gratuita de 15 minutos te mostrará exactamente dónde Kitxens mueve tu restaurante de invisible al top en búsqueda IA.',
    ctaPrimary: 'Auditoría de Visibilidad IA',
    ctaSecondary: 'Llamada de Estrategia IA',
  },
};

// =================== Mock dashboard data (display only) ===================
const DASH_ENGINES = [
  { name: 'ChatGPT', pct: 38, tone: 'bg-emerald-500' },
  { name: 'Gemini', pct: 26, tone: 'bg-blue-500' },
  { name: 'Perplexity', pct: 18, tone: 'bg-indigo-500' },
  { name: 'Google AI', pct: 12, tone: 'bg-rose-500' },
  { name: 'Claude', pct: 6, tone: 'bg-orange-500' },
];

// Official brand marks per AI engine / platform name
const ENGINE_MARKS = {
  ChatGPT: ChatGPTMark,
  Gemini: GeminiMark,
  Claude: ClaudeMark,
  Perplexity: PerplexityMark,
  'Google AI': GoogleMark,
  Grok: GrokMark,
};

const TRUST_MARKS = {
  Google: GoogleMark,
  OpenAI: OpenAIMark,
  Meta: MetaMark,
  Toast: ToastMark,
  Square: SquareMark,
  DoorDash: DoorDashMark,
  'Uber Eats': UberEatsMark,
  OpenTable: OpenTableMark,
  'Google Business Profile': GoogleMark,
  Yelp: YelpMark,
};

export default function IndexAiPage() {
  const { lang } = useT();
  const c = C[lang] || C.en;

  const pageUrl = 'https://www.kitxens.com/index-ai';
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://www.kitxens.com/#organization',
        name: 'Kitxens',
        url: 'https://www.kitxens.com',
        logo: 'https://www.kitxens.com/favicon.svg',
        description: 'Restaurant AI platform connecting every operation, channel and growth signal into one managed service.',
        areaServed: ['United States', 'Canada', 'Mexico', 'Spain', 'Latin America', 'Europe'],
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+1-415-598-2310',
          contactType: 'sales',
          areaServed: ['US', 'CA', 'MX', 'ES'],
          availableLanguage: ['English', 'Spanish'],
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: `Index AI · ${lang === 'es' ? 'Visibilidad IA para restaurantes' : 'AI Visibility for Restaurants'}`,
        description: c.heroBody,
        inLanguage: lang === 'es' ? 'es' : 'en',
        isPartOf: { '@type': 'WebSite', url: 'https://www.kitxens.com', name: 'Kitxens' },
        primaryImageOfPage: 'https://www.kitxens.com/favicon.svg',
        about: { '@id': `${pageUrl}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.kitxens.com/' },
          { '@type': 'ListItem', position: 2, name: 'AI Solutions', item: 'https://www.kitxens.com/index-ai' },
          { '@type': 'ListItem', position: 3, name: 'Index AI', item: pageUrl },
        ],
      },
      {
        '@type': 'Service',
        '@id': `${pageUrl}#service`,
        name: 'Index AI — Managed Restaurant AI Visibility',
        serviceType: 'AI Visibility and Discovery for Restaurants',
        provider: { '@id': 'https://www.kitxens.com/#organization' },
        areaServed: ['United States', 'Canada', 'Mexico', 'Spain', 'Latin America', 'Europe'],
        description: c.tldr,
        url: pageUrl,
        category: 'Restaurant Technology · Generative Engine Optimization · Local SEO',
        offers: {
          '@type': 'Offer',
          '@id': `${pageUrl}#offer`,
          url: 'https://www.kitxens.com/pricing',
          priceCurrency: 'USD',
          price: '199',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '199',
            priceCurrency: 'USD',
            unitText: 'MONTH',
            referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
          },
          availability: 'https://schema.org/InStock',
          businessFunction: 'https://schema.org/Sell',
          seller: { '@id': 'https://www.kitxens.com/#organization' },
          eligibleRegion: ['United States', 'Canada', 'Mexico', 'Spain'],
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${pageUrl}#faq`,
        mainEntity: c.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="bg-white" data-testid="index-ai-page">
      <SEO title="Is AI Recommending Your Restaurant? Free Index AI™ Scan" description={c.heroBody} path="/index-ai" schema={schema} />

      {/* ========== 1. Hero ========== */}
      <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-24 overflow-hidden bg-gradient-to-b from-[#0F1117] via-[#0F1117] to-[#1A1D26] text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 18% 22%, #F77F11 1px, transparent 1.5px), radial-gradient(circle at 80% 70%, #F77F11 1px, transparent 1.5px)',
            backgroundSize: '110px 110px, 160px 160px',
          }}
        />
        <div aria-hidden="true" className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-30 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(247,127,17,0.32), transparent 70%)' }} />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }} className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 bg-[#F77F11]/15 border border-[#F77F11]/30 text-[#FFB672] text-[11px] tracking-[0.22em] font-semibold uppercase px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> {c.eyebrow}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.05] tracking-[-0.025em]">{c.heroTitle}</h1>
            <p className="mt-6 text-neutral-300 text-base sm:text-lg leading-relaxed max-w-2xl">{c.heroBody}</p>
            <p className="mt-4 text-neutral-400 text-[14.5px] leading-relaxed max-w-2xl">{c.heroSub}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#scan" className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-[0_15px_40px_-15px_rgba(247,127,17,0.6)] hover:shadow-[0_20px_50px_-15px_rgba(247,127,17,0.7)] transition-all" data-testid="index-ai-cta-primary">
                {lang === 'es' ? 'Escanea tu web gratis' : 'Scan your website free'} <ArrowRight className="w-4 h-4 ml-1" />
              </a>
              <Link to="/contact#contact-form" className="inline-flex items-center gap-1 border border-white/25 hover:border-[#F77F11] hover:text-[#F77F11] text-white h-12 px-7 rounded-full text-[15px] font-semibold transition-colors" data-testid="index-ai-cta-secondary">
                {c.ctaB} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </motion.div>

          {/* Right: Mock AI answer card */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5">
            <div className="relative rounded-3xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 backdrop-blur-md p-5 sm:p-6 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]">
              {/* Prompt bar */}
              <div className="flex items-center gap-2.5 rounded-xl bg-black/30 border border-white/10 px-3.5 py-2.5">
                <Search className="w-4 h-4 text-white/40 shrink-0" />
                <p className="text-white/85 text-[13.5px] font-medium truncate min-w-0">
                  {lang === 'es' ? '¿Mejor brunch cerca de Mountain View?' : 'Best brunch near Mountain View?'}
                </p>
                <span className="ml-auto text-[10px] tracking-[0.18em] font-semibold uppercase text-white/40 hidden sm:inline">ChatGPT</span>
              </div>

              {/* AI answer block */}
              <div className="mt-4 rounded-xl bg-black/20 border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </span>
                  <span className="text-white/70 text-[12px] font-semibold tracking-[-0.005em]">{lang === 'es' ? 'Respuesta de IA' : 'AI answer'}</span>
                  <span className="ml-auto inline-flex items-center gap-1.5 text-[10.5px] text-emerald-300 font-semibold">
                    <span className="relative flex w-2 h-2">
                      <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
                      <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
                    </span>
                    {lang === 'es' ? 'En vivo' : 'Live'}
                  </span>
                </div>
                <p className="text-white/85 text-[13px] leading-[1.65]">
                  {lang === 'es'
                    ? 'Una buena opción cerca de Mountain View es '
                    : 'A great option near Mountain View is '}
                  <span className="inline-flex items-center gap-1 bg-[#F77F11]/20 border border-[#F77F11]/40 text-[#FFB672] font-semibold px-1.5 py-0.5 rounded-md">
                    <Flame className="w-3 h-3" /> {lang === 'es' ? 'Tu Restaurante' : 'Your Restaurant'}
                  </span>
                  {lang === 'es'
                    ? ' — brunch artesanal, terraza abierta, reservas en OpenTable y reseñas promedio de 4.8.'
                    : ' — artisan brunch, open terrace, OpenTable reservations and a 4.8 average rating.'}
                </p>
              </div>

              {/* Sources */}
              <div className="mt-4 flex items-center justify-between gap-3 text-[11.5px]">
                <p className="flex items-center gap-1.5 text-white/40 min-w-0">
                  <span className="font-semibold text-white/60 shrink-0">{lang === 'es' ? 'Fuentes' : 'Sources'}:</span>
                  <GoogleMark className="!w-4 !h-4 shrink-0" />
                  <YelpMark className="!w-4 !h-4 shrink-0" />
                  <OpenTableMark className="!w-4 !h-4 shrink-0" />
                  <span className="truncate">Schema.org</span>
                </p>
                <span className="inline-flex items-center gap-1 text-[#FFB672] font-semibold shrink-0">
                  {lang === 'es' ? 'Indexado por Kitxens' : 'Indexed by Kitxens'} <CheckCircle2 className="w-3 h-3" />
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== 1.5 Free live scanner — Kitxens Index AI Score™ ========== */}
      <IndexAiScanner />

      {/* ========== 1.6 Head-to-head vs your competitor ========== */}
      <CompetitorCompare />

      {/* ========== 2. Definition block — premium executive summary ========== */}
      <section className="py-16 lg:py-20" data-testid="index-ai-definition">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.defnEyebrow}</p>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">
                {c.defnTitle}
              </h2>
              {/* AI-quotable TL;DR — semantic blockquote so AI engines pick it up as the canonical definition */}
              <blockquote
                className="mt-6 relative rounded-2xl bg-orange-50/60 border-l-4 border-[#F77F11] px-6 py-5 text-neutral-800 text-[15.5px] leading-[1.7]"
                data-testid="index-ai-tldr"
                cite={pageUrl}
              >
                <span className="absolute -top-3 left-5 text-[10px] tracking-[0.24em] font-bold uppercase text-[#F77F11] bg-white px-2 py-0.5 rounded-full border border-orange-200">
                  TL;DR
                </span>
                {c.tldr}
              </blockquote>

              {/* Subtle contextual links — natural, not forced */}
              <p className="mt-5 text-neutral-500 text-[13.5px] leading-relaxed">
                {lang === 'es'
                  ? <>Index AI funciona como una capa de visibilidad continua, complementando a nuestros <Link to="/ai-agents-for-restaurants" className="text-[#F77F11] hover:text-[#d8651a] font-semibold underline-offset-4 hover:underline">AI Agents</Link> y los planes mensuales descritos en <Link to="/pricing" className="text-[#F77F11] hover:text-[#d8651a] font-semibold underline-offset-4 hover:underline">Precios</Link>.</>
                  : <>Index AI runs as a continuous visibility layer that pairs naturally with our <Link to="/ai-agents-for-restaurants" className="text-[#F77F11] hover:text-[#d8651a] font-semibold underline-offset-4 hover:underline">AI Agents</Link> and the monthly plans documented on <Link to="/pricing" className="text-[#F77F11] hover:text-[#d8651a] font-semibold underline-offset-4 hover:underline">Pricing</Link>.</>
                }
              </p>
            </div>

            {/* Three quotable mini-cards — each one a stand-alone AI-citable concept */}
            <div className="lg:col-span-5 space-y-3">
              {c.defnCards.map((d, i) => (
                <motion.div
                  key={d.t}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="group rounded-2xl border border-neutral-200 bg-white p-5 hover:border-[#F77F11] hover:shadow-[0_18px_50px_-25px_rgba(247,127,17,0.35)] transition-all duration-300"
                >
                  <p className="text-neutral-900 font-semibold text-[15px] tracking-[-0.005em] leading-snug">{d.t}</p>
                  <p className="mt-2 text-neutral-600 text-[13.5px] leading-relaxed">{d.b}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== 3. AI Engines bar ========== */}
      <section className="py-16 lg:py-20" data-testid="index-ai-engines">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.15]">{c.enginesTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15px] leading-relaxed">{c.enginesBody}</p>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {c.engines.map((e, i) => {
              const Mark = ENGINE_MARKS[e.name];
              return (
                <motion.div
                  key={e.name}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  whileHover={{ y: -3 }}
                  className="group flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-5 py-3 hover:border-[#F77F11] hover:shadow-[0_20px_50px_-25px_rgba(247,127,17,0.4)] transition-all duration-300"
                  data-testid={`index-ai-engine-${e.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {Mark ? (
                    <Mark className="!w-8 !h-8 rounded-lg shadow-sm" />
                  ) : (
                    <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${e.tone} flex items-center justify-center shadow-md`}>
                      <Sparkles className="w-4 h-4 text-white" />
                    </span>
                  )}
                  <p className="text-neutral-900 font-semibold text-[14.5px] tracking-[-0.005em]">{e.name}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== 3. How AI Search Works ========== */}
      <section className="py-20 lg:py-24 bg-neutral-50/60" data-testid="index-ai-flow">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.flowEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.flowTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.flowBody}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-6 gap-4 relative">
            {c.flow.map((s, i) => {
              const I = s.i;
              const isYou = i === 2; // Highlight "Kitxens prepares your restaurant"
              return (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group relative rounded-2xl border p-5 transition-all duration-300 ${
                    isYou
                      ? 'border-[#F77F11] bg-orange-50/60 shadow-[0_24px_60px_-30px_rgba(247,127,17,0.45)]'
                      : 'border-neutral-200 bg-white hover:border-orange-200 hover:shadow-[0_18px_50px_-25px_rgba(247,127,17,0.3)]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${isYou ? 'bg-[#F77F11] text-white' : 'bg-orange-50 text-[#F77F11] group-hover:bg-[#F77F11] group-hover:text-white'} transition-colors duration-300`}>
                      <I className="w-5 h-5" />
                    </span>
                    <span className="text-[10px] tracking-[0.22em] font-bold text-neutral-300">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <p className="mt-4 text-neutral-900 font-semibold text-[14.5px] tracking-[-0.005em] leading-snug">{s.t}</p>
                  <p className="mt-1.5 text-neutral-600 text-[12.5px] leading-snug italic">{s.b}</p>
                  {/* Connector arrow on lg */}
                  {i < c.flow.length - 1 && (
                    <ChevronRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-300" aria-hidden="true" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== 4. What Kitxens does ========== */}
      <section className="py-20 lg:py-24" data-testid="index-ai-services">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.servicesEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.servicesTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.servicesBody}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.services.map((s, i) => {
              const I = s.i;
              return (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  whileHover={{ y: -3 }}
                  className="group rounded-2xl border border-neutral-200 bg-white p-7 hover:border-[#F77F11] hover:shadow-[0_24px_60px_-30px_rgba(247,127,17,0.35)] transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="inline-flex w-11 h-11 rounded-xl bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors duration-300">
                      <I className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors duration-300" />
                    </span>
                    <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#F77F11] bg-orange-50 border border-orange-100 rounded-full px-2.5 py-1">{s.tag}</span>
                  </div>
                  <h3 className="text-neutral-900 font-semibold text-[17px] tracking-[-0.005em] leading-snug">{s.t}</h3>
                  <p className="mt-2 text-neutral-600 text-[14px] leading-relaxed">{s.b}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== 5. Process ========== */}
      <section className="py-20 lg:py-24 bg-neutral-50/60" data-testid="index-ai-process">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.processEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.processTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.processBody}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.process.map((s, i) => {
              const I = s.i;
              return (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] transition-all duration-300"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F77F11] text-white text-[12.5px] font-bold">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="inline-flex w-9 h-9 rounded-lg bg-orange-50 items-center justify-center">
                      <I className="w-4.5 h-4.5 text-[#F77F11]" />
                    </span>
                  </div>
                  <h3 className="mt-5 text-neutral-900 font-semibold text-[16px] tracking-[-0.005em]">{s.t}</h3>
                  <p className="mt-2 text-neutral-600 text-[13.5px] leading-relaxed">{s.b}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== 6. Dashboard Mockup ========== */}
      <section className="py-20 lg:py-24 bg-[#0F1117] text-white" data-testid="index-ai-dashboard">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white/90 text-[11px] tracking-[0.22em] font-semibold uppercase px-3 py-1.5 rounded-full">
              <BarChart3 className="w-3.5 h-3.5 text-[#F77F11]" /> {c.dashEyebrow}
            </span>
            <h2 className="mt-6 text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-[-0.02em] leading-[1.1]">{c.dashTitle}</h2>
            <p className="mt-5 text-neutral-300 text-[15.5px] leading-relaxed max-w-lg">{c.dashBody}</p>
            <ul className="mt-7 space-y-3">
              {[
                lang === 'es' ? 'Score de Visibilidad IA' : 'AI Visibility Score',
                lang === 'es' ? 'Menciones por buscador' : 'Mentions by engine',
                lang === 'es' ? 'Share of Voice' : 'Share of Voice',
                lang === 'es' ? 'Tráfico IA y conversión' : 'AI traffic and conversion',
              ].map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-[14px] text-neutral-200">
                  <CheckCircle2 className="w-4 h-4 text-[#F77F11]" /> {b}
                </li>
              ))}
            </ul>
          </div>

          {/* Dashboard card */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 backdrop-blur-md shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)] overflow-hidden">
              {/* Top bar */}
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10 bg-black/30">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                <span className="ml-3 text-[11px] tracking-[0.22em] font-semibold uppercase text-white/40">kitxens.ai · visibility</span>
                <span className="ml-auto text-[10.5px] text-emerald-300 font-semibold inline-flex items-center gap-1">
                  <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" /><span className="relative w-2 h-2 rounded-full bg-emerald-400" /></span>
                  live
                </span>
              </div>

              <div className="p-5 sm:p-7 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {/* Score card */}
                <div className="col-span-2 sm:col-span-2 rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                  <div className="flex items-start justify-between">
                    <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/40">AI Visibility Score</p>
                    <span className="inline-flex items-center gap-1 text-emerald-300 text-[11px] font-semibold">
                      <TrendingUp className="w-3 h-3" /> +12 pts
                    </span>
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <p className="text-[#F77F11] text-[44px] sm:text-[52px] font-bold leading-none tracking-[-0.025em]">87</p>
                    <p className="text-white/40 text-[12px] font-semibold">/ 100</p>
                  </div>
                  {/* Bar */}
                  <div className="mt-5 h-2 rounded-full bg-white/5 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '87%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="h-full bg-gradient-to-r from-[#F77F11] to-[#FFB672] rounded-full"
                    />
                  </div>
                  <p className="mt-3 text-white/50 text-[11.5px]">{lang === 'es' ? 'Vs. competidores de tu zona' : 'Vs. competitors in your area'}</p>
                </div>

                {/* Citations */}
                <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                  <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/40">AI Citations</p>
                  <p className="mt-3 text-white text-[28px] font-bold leading-none tracking-[-0.02em]">142</p>
                  <p className="mt-1 text-emerald-300 text-[11px] font-semibold">+38 vs prev. month</p>
                  {/* Sparkline */}
                  <svg viewBox="0 0 80 28" className="mt-3 w-full h-7">
                    <polyline points="0,22 12,18 24,20 36,12 48,15 60,8 72,10 80,4" fill="none" stroke="#F77F11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>

                {/* Engine breakdown */}
                <div className="col-span-2 sm:col-span-3 rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/40">Share of voice — AI engines</p>
                    <PieChart className="w-4 h-4 text-white/30" />
                  </div>
                  <div className="space-y-3">
                    {DASH_ENGINES.map((e, i) => {
                      const Mark = ENGINE_MARKS[e.name];
                      return (
                        <div key={e.name} className="flex items-center gap-3">
                          <span className="flex items-center gap-2 w-24 sm:w-32 min-w-0">
                            {Mark && <Mark className="!w-4 !h-4 shrink-0" />}
                            <p className="text-[12.5px] text-white/80 font-medium truncate">{e.name}</p>
                          </span>
                          <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              whileInView={{ width: `${e.pct}%` }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.9, delay: i * 0.08, ease: 'easeOut' }}
                              className={`${e.tone} h-full rounded-full`}
                            />
                          </div>
                          <p className="w-10 text-right text-[12px] text-white/60 font-semibold">{e.pct}%</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 7. Restaurant prompt examples ========== */}
      <section className="py-20 lg:py-24" data-testid="index-ai-prompts">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-10">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.promptsEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.promptsTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.promptsBody}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.prompts.map((p, i) => (
              <motion.div
                key={p.q}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                whileHover={{ y: -3 }}
                className="group rounded-2xl border border-neutral-200 bg-white p-5 hover:border-[#F77F11] hover:shadow-[0_18px_50px_-25px_rgba(247,127,17,0.35)] transition-all duration-300 min-w-0"
              >
                <div className="flex items-center gap-2.5 rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2.5 min-w-0">
                  <Search className="w-4 h-4 text-neutral-400 shrink-0" />
                  <p className="text-neutral-900 text-[13px] font-medium truncate min-w-0">{p.q}</p>
                </div>
                <div className="mt-4 flex items-start gap-2.5">
                  <span className="mt-0.5 w-6 h-6 rounded-md bg-gradient-to-br from-[#F77F11] to-[#FFB672] flex items-center justify-center shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </span>
                  <p className="text-neutral-700 text-[13.5px] leading-snug">{p.a}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 8. SEO vs Index AI ========== */}
      <section className="py-20 lg:py-24 bg-neutral-50/60" data-testid="index-ai-comparison">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-10">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.cmpEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.cmpTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.cmpBody}</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Traditional SEO */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45 }}
              className="rounded-3xl border border-neutral-200 bg-white p-8 opacity-90"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex w-11 h-11 rounded-xl bg-neutral-100 items-center justify-center">
                  <Globe2 className="w-5 h-5 text-neutral-500" />
                </span>
                <p className="text-neutral-500 text-[11px] tracking-[0.22em] font-bold uppercase">{lang === 'es' ? 'Cómo era antes' : 'How it used to work'}</p>
              </div>
              <h3 className="mt-5 text-2xl font-semibold text-neutral-700 tracking-[-0.01em]">{c.cmp.old.t}</h3>
              <p className="mt-3 text-neutral-500 text-[14.5px] leading-relaxed">{c.cmp.old.b}</p>
              <ul className="mt-6 space-y-3">
                {c.cmp.old.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-[14px] text-neutral-500">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-neutral-300 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Index AI */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="rounded-3xl border-2 border-[#F77F11] bg-gradient-to-br from-white to-orange-50/40 p-8 shadow-[0_30px_70px_-30px_rgba(247,127,17,0.4)]"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="inline-flex w-11 h-11 rounded-xl bg-[#F77F11] items-center justify-center shadow-md">
                    <Sparkles className="w-5 h-5 text-white" />
                  </span>
                  <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">{lang === 'es' ? 'El nuevo playbook' : 'The new playbook'}</p>
                </div>
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#F77F11] bg-white border border-orange-200 rounded-full px-2.5 py-1">Kitxens</span>
              </div>
              <h3 className="mt-5 text-2xl font-bold text-neutral-900 tracking-[-0.01em]">{c.cmp.ai.t}</h3>
              <p className="mt-3 text-neutral-700 text-[14.5px] leading-relaxed">{c.cmp.ai.b}</p>
              <ul className="mt-6 space-y-3">
                {c.cmp.ai.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-[14.5px] text-neutral-800">
                    <CheckCircle2 className="mt-0.5 w-4 h-4 shrink-0 text-[#F77F11]" />
                    <span className="font-medium">{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== 9. Business outcomes ========== */}
      <section className="py-20 lg:py-24" data-testid="index-ai-outcomes">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.outcomesEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.outcomesTitle}</h2>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {c.outcomes.map((o, i) => {
              const I = o.i;
              return (
                <motion.div
                  key={o.t}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  whileHover={{ y: -3 }}
                  className="group flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4 hover:border-[#F77F11] hover:bg-orange-50/40 transition-all duration-300"
                >
                  <span className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center shrink-0 group-hover:bg-[#F77F11] transition-colors duration-300">
                    <I className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors duration-300" />
                  </span>
                  <p className="text-neutral-900 font-semibold text-[14px] tracking-[-0.005em] leading-snug">{o.t}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== 10. Service Highlights ========== */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-[#0F1117] via-[#161A22] to-[#0F1117] text-white" data-testid="index-ai-highlights">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#FFB672] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.highlightsEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-[-0.02em] leading-[1.1]">{c.highlightsTitle}</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.highlights.map((h, i) => {
              const I = h.i;
              return (
                <motion.div
                  key={h.t}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:border-[#F77F11]/40 hover:bg-white/[0.07] transition-all duration-300"
                >
                  <span className="inline-flex w-11 h-11 rounded-xl bg-[#F77F11]/15 border border-[#F77F11]/30 items-center justify-center">
                    <I className="w-5 h-5 text-[#F77F11]" />
                  </span>
                  <p className="mt-5 text-white font-semibold text-[16px] tracking-[-0.005em]">{h.t}</p>
                  <p className="mt-1.5 text-neutral-300 text-[13.5px] leading-relaxed">{h.b}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Trust strip */}
          <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-7">
              <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/45 shrink-0 sm:border-r sm:border-white/10 sm:pr-7">
                {c.trustLabel}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {c.trust.map((name) => {
                  const Mark = TRUST_MARKS[name];
                  return (
                    <span
                      key={name}
                      className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-white/75 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#F77F11]/40 rounded-full px-3.5 py-1.5 transition-all duration-300"
                      data-testid={`index-trust-${name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {Mark && <Mark className="!w-4 !h-4 shrink-0" />}
                      {name}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ — restaurant-owner questions, AI-citable ========== */}
      <section className="py-20 lg:py-24" data-testid="index-ai-faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.faqEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.015em] leading-tight">
              {c.faqTitle}
            </h2>
            <p className="mt-4 text-neutral-600 text-[15px] leading-relaxed">{c.faqBody}</p>
          </div>
          <Accordion type="single" collapsible className="mt-10 w-full divide-y divide-neutral-200 border-y border-neutral-200">
            {c.faq.map((f, i) => (
              <AccordionItem
                key={`ixfaq-${i}`}
                value={`ixfaq-${i}`}
                className="border-b-0"
                data-testid={`index-ai-faq-item-${i}`}
              >
                <AccordionTrigger className="py-5 text-[16px] font-semibold text-neutral-900 hover:no-underline hover:text-[#F77F11] text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-[15px] text-neutral-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Lightweight contextual link out — natural, not forced */}
          <p className="mt-10 text-center text-neutral-500 text-[13.5px]">
            {lang === 'es'
              ? <>Más detalles del programa en <Link to="/how-it-works" className="text-[#F77F11] hover:text-[#d8651a] font-semibold underline-offset-4 hover:underline">Cómo Funciona</Link> · Precios en <Link to="/pricing" className="text-[#F77F11] hover:text-[#d8651a] font-semibold underline-offset-4 hover:underline">/pricing</Link>.</>
              : <>More program details in <Link to="/how-it-works" className="text-[#F77F11] hover:text-[#d8651a] font-semibold underline-offset-4 hover:underline">How It Works</Link> · Pricing in <Link to="/pricing" className="text-[#F77F11] hover:text-[#d8651a] font-semibold underline-offset-4 hover:underline">/pricing</Link>.</>
            }
          </p>
        </div>
      </section>

      {/* ========== 10.5. Powered by Penny — bridge to Operating Team ========== */}
      <section className="bg-white py-14 lg:py-16 border-t border-neutral-200">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <Link
            to="/operating-intelligence#penny"
            className="group flex items-center gap-4 sm:gap-6 rounded-2xl border border-neutral-200 bg-neutral-50/60 hover:bg-orange-50/40 hover:border-[#F77F11] hover:shadow-[0_24px_60px_-30px_rgba(247,127,17,0.35)] p-5 sm:p-6 transition-all"
            data-testid="index-ai-penny-bridge"
          >
            <span className="relative inline-flex w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-violet-400 to-violet-600 overflow-hidden shrink-0 shadow-[0_0_0_4px_rgba(167,139,250,0.18)]">
              <img
                src={TEAM_AVATARS.find((a) => a.key === 'penny')?.img}
                alt="Penny"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#F77F11]">
                {lang === 'es' ? 'POTENCIADO POR EL EQUIPO OPERATIVO' : 'POWERED BY THE OPERATING TEAM'}
              </p>
              <p className="mt-1.5 text-neutral-900 text-base sm:text-lg font-semibold tracking-[-0.01em] leading-snug">
                {lang === 'es'
                  ? 'Index AI lo escribe y actualiza Penny, nuestra especialista en contenido SEO.'
                  : 'Index AI is written and continuously updated by Penny, our SEO Content specialist.'}
              </p>
              <p className="mt-1 text-neutral-600 text-[13.5px] leading-relaxed">
                {lang === 'es'
                  ? 'Es una de los seis especialistas de IA que operan detrás de cada experiencia Kitxens.'
                  : 'One of six AI specialists operating behind every Kitxens experience.'}
              </p>
            </div>
            <ArrowUpRight className="hidden sm:inline-flex w-5 h-5 text-[#F77F11] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>
      </section>

      {/* ========== 11. Final CTA ========== */}
      <section className="py-20 lg:py-24 bg-[#0F1117] text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.015em]">{c.ctaTitle}</h2>
          <p className="mt-5 text-neutral-300 text-[15.5px] leading-relaxed">{c.ctaBody}</p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <Link to="/#audit" className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm hover:shadow-md transition-all" data-testid="index-ai-bottom-cta">
              {c.ctaPrimary} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact#contact-form" className="inline-flex items-center gap-2 border border-neutral-700 text-neutral-200 hover:border-[#F77F11] hover:text-[#F77F11] px-6 h-12 rounded-full text-[14px] font-semibold transition-colors" data-testid="index-ai-bottom-cta-secondary">
              {c.ctaSecondary}
            </Link>
          </div>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-[12.5px] text-neutral-400">
            <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><MessageCircle className="w-4 h-4 text-[#F77F11]" /> WhatsApp</a>
            <a href="tel:+14155982310" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Phone className="w-4 h-4 text-[#F77F11]" /> +1 (415) 598-2310</a>
            <a href="mailto:hello@kitxens.com" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Mail className="w-4 h-4 text-[#F77F11]" /> hello@kitxens.com</a>
          </div>
        </div>
      </section>

      <ProductPricingCTA product="indexai" />

      <FlagshipStrip current="indexai" />
      <AffiliateStrip className="py-14" />
      <ContinueExploring />
    </div>
  );
}
